import { useQueries } from "@tanstack/react-query";
import { Collapsible, LoadingSpinner, Text } from "@wojo/ui";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React from "react";
import { GiHealthCapsule } from "react-icons/gi";
import { HiOutlineDocumentText } from "react-icons/hi";
import { RiSurgicalMaskLine } from "react-icons/ri";
import {
    categorizeInclusion,
    EntryRequirementsInclusion,
} from "./EntryRequirementsInclusion";
import styles from "./EntryRequirementsSegments.module.scss";
import { SherpaInclusion } from "./SherpaInclusion";
import { useCountriesAndStates } from "client/hooks";

dayjs.extend(utc);

export type EntryRequirementsSegments = {
    activeDate: string;
    destinationCode: string;
    nationalityCode: string;
    nextDestinationCode?: string;
    originCode: string;
    travelHistory: string[];
};

export type EntryRequirementsSegmentsProps = {
    /** The home country code for the traveler or market, i.e. USA or CAN */
    nationalityCountryCode: string;
    /** The country codes for each destination, in order of travel */
    countryCodes: string[];
    /** The intended date of travel. Defaults to today. */
    travelDate?: string;
    /** The country code for origin of these segments.  Defaults to nationalityCountryCode */
    originCountryCode?: string;
};

export const EntryRequirementsSegments: React.FC<
    EntryRequirementsSegmentsProps
> = ({
    countryCodes,
    nationalityCountryCode,
    travelDate,
    originCountryCode,
}) => {
    const activeDate = dayjs.utc(travelDate).format("YYYY-MM-DD");
    const segments = buildSegments(
        countryCodes,
        originCountryCode ?? nationalityCountryCode,
        nationalityCountryCode,
        activeDate,
    );
    const originCountryName = useCountriesAndStates().getCountryName(
        originCountryCode ?? nationalityCountryCode,
    );
    const results = useQueries({
        queries: segments.map((segment) => {
            return {
                queryKey: [
                    "sherpa",
                    ...segment.travelHistory,
                    segment.destinationCode,
                ],
                queryFn: async () => {
                    const params = new URLSearchParams({
                        "filter[procedure.originCountries]": segment.originCode,
                        "filter[procedure.nationalities]":
                            segment.nationalityCode,
                        "filter[procedure.activeDate]": segment.activeDate,
                        "filter[travelHistory]":
                            segment.travelHistory.join(","),
                        include: "procedure",
                        affiliateId: "efworldjourneys",
                        key: process.env.NEXT_PUBLIC_SHERPA_API_KEY || "",
                    });
                    if (segment.nextDestinationCode) {
                        params.set(
                            "filter[procedure.destinationCountries]",
                            segment.nextDestinationCode,
                        );
                    }
                    const response = await fetch(
                        `https://requirements-api.joinsherpa.com/v2/countries/${
                            segment.destinationCode
                        }?${params.toString()}`,
                    );

                    if (!response.ok) {
                        return null;
                    }
                    return response.json();
                },
            };
        }),
    });

    if (results.some((result) => result.isLoading)) {
        return (
            <div className={styles.spinner}>
                <LoadingSpinner
                    accessibleText="Loading entry requirements"
                    palette="primary"
                    size="60px"
                />
            </div>
        );
    }

    return (
        <div style={{ marginBottom: "var(--g-spacing-xl)" }}>
            {results.map((result, index) => {
                const inclusions = result.data?.included;
                const origin =
                    results[index - 1]?.data?.data?.attributes?.countryName ||
                    originCountryName;
                const destination = result.data?.data?.attributes?.countryName;
                const title = (
                    <span>
                        {origin} to <strong>{destination}</strong>
                    </span>
                );

                return (
                    <Collapsible key={index} title={title}>
                        <Requirements inclusions={inclusions} />
                    </Collapsible>
                );
            })}
        </div>
    );
};

const Requirements: React.FC<{ inclusions: SherpaInclusion[] }> = ({
    inclusions,
}) => {
    const ppe = inclusions.find(
        (inclusion) => categorizeInclusion(inclusion) === "PPE",
    );
    const test = inclusions.find(
        (inclusion) => categorizeInclusion(inclusion) === "Test",
    );
    const basicDocs = inclusions.filter(
        (inclusion) => categorizeInclusion(inclusion) === "BasicDocs",
    );
    const additionalDocs = inclusions.filter(
        (inclusion) => categorizeInclusion(inclusion) === "AdditionalDocs",
    );
    const hasBasicRequirements = !!ppe || !!test || !!basicDocs.length;

    if (!hasBasicRequirements && !additionalDocs.length) {
        return (
            <Text.Body>
                There are no travel restrictions at this time. Please note that
                tour participation requirements still apply.
            </Text.Body>
        );
    }
    return (
        <div>
            {hasBasicRequirements && (
                <>
                    <Text.Body className={styles.subheading} emphasis="2">
                        Basic requirements
                    </Text.Body>
                    <div className={styles.inclusions}>
                        {ppe && (
                            <EntryRequirementsInclusion
                                icon={<RiSurgicalMaskLine size={20} />}
                                inclusion={ppe}
                                title="Masks"
                            />
                        )}
                        {test && (
                            <EntryRequirementsInclusion
                                icon={<GiHealthCapsule size={20} />}
                                inclusion={test}
                                title="COVID-19 Test"
                            />
                        )}
                        {basicDocs.map((inclusion) => (
                            <EntryRequirementsInclusion
                                icon={<HiOutlineDocumentText size={20} />}
                                inclusion={inclusion}
                                key={inclusion.id}
                                title={documentTitle(
                                    inclusion.attributes.documentType?.[0],
                                )}
                            />
                        ))}
                    </div>
                </>
            )}
            {!!additionalDocs.length && (
                <>
                    <Text.Body className={styles.subheading} emphasis="2">
                        Additional documents
                    </Text.Body>
                    <div className={styles.inclusions}>
                        {additionalDocs.map((inclusion) => (
                            <EntryRequirementsInclusion
                                icon={<HiOutlineDocumentText size={20} />}
                                inclusion={inclusion}
                                key={inclusion.id}
                                title={documentTitle(
                                    inclusion.attributes.documentType?.[0],
                                )}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const buildSegments = (
    countryCodes: string[] | undefined,
    initialCountryCode: string,
    nationalityCountryCode: string,
    activeDate: string,
): EntryRequirementsSegments[] => {
    if (!countryCodes?.length) {
        return [];
    }

    const tourSegments = countryCodes.reduce<EntryRequirementsSegments[]>(
        (acc, countryCode) => {
            const prev = acc.pop();
            if (prev) {
                acc.push({ ...prev, nextDestinationCode: countryCode });
            }
            const travelHistory = [
                nationalityCountryCode,
                ...acc.map((segment) => segment.destinationCode),
            ];
            acc.push({
                activeDate,
                destinationCode: countryCode,
                nationalityCode: nationalityCountryCode,
                originCode: prev?.destinationCode || initialCountryCode,
                travelHistory,
            });

            return acc;
        },
        [],
    );
    const lastDestination = tourSegments[tourSegments.length - 1];
    if (lastDestination.destinationCode !== nationalityCountryCode) {
        tourSegments.push({
            activeDate,
            destinationCode: nationalityCountryCode,
            nationalityCode: nationalityCountryCode,
            originCode: lastDestination.destinationCode,
            travelHistory: [
                nationalityCountryCode,
                ...tourSegments.map((segment) => segment.destinationCode),
            ],
        });
    }
    return tourSegments;
};

const documentTitle = (documentType: string | undefined): string => {
    switch (documentType) {
        case "COVID_TEST_RESULT":
            return "COVID Test Result";
        case "COVID_RECOVERY_CERTIFICATE":
        case "COVID_RECOVERY_CERTIFICATION":
        case "MEDICAL_CERTIFICATE":
        case "HEALTH_CERTIFICATE":
            return "Health Certificate";
        case "PASSENGER_LOCATOR_FORM":
            return "Contact Tracing";
        case "HEALTH_DECLARATION":
        case "TRAVEL_AUTHORIZATION":
            return "Health Declaration";
        case "COVID_19_VACCINATION":
            return "Proof of Vaccination";
        case "IMMUNITY_PROOF":
            return "Proof of Immunization";
        case "COVID_19_INSURANCE":
            return "Insurance";
        case "TRAVEL_DECLARATION":
            return "Travel Declaration";
        case "MOBILE_APP":
            return "Mobile App";
        case "HEALTH_PASS":
            return "Health Pass";
        case "E_VISA":
        case "EMBASSY_VISA":
        case "VISA":
            return "Visa";
        case "PRE_REGISTRATION_FORM":
            return "Pre-registration";
        case "ETA":
        case "ACCOMMODATION_BOOKING":
        case "QUARANTINE_FORM":
        case "EXEMPTION_FORM":
        case "RE_ENTRY_PERMIT":
        default:
            return "Other";
    }
};
